import type { BundleFileStore } from './bundleFileStore'
import { gunzip } from './gunzip'
import type { ManifestFile } from './remoteManifest'
import { sha256Hex } from './sha256Hex'

export type DownloadOutcome =
  | {
      readonly kind: 'downloaded'
      /** Every requested file, unpacked, by name. */
      readonly files: ReadonlyMap<string, ArrayBuffer>
      /** Compressed bytes fetched over the network (reused files excluded). */
      readonly fetchedBytes: number
    }
  /** The network failed — nothing is wrong with the bundle; try again later. */
  | { readonly kind: 'failed'; readonly reason: string }
  /** A file's bytes are not what the manifest says (spec §3.5 step 1). */
  | { readonly kind: 'rejected'; readonly check: 'gzip_sha256' | 'sha256' }

/**
 * Brings every file of a bundle into the store and returns their unpacked
 * bytes for verification. A file the store already holds (any earlier bundle
 * or download) is reused, not fetched; a fetched one must match `gzipSha256`
 * as downloaded and `sha256`/`size` once unpacked before it is stored.
 */
export async function downloadBundleFiles(
  files: readonly ManifestFile[],
  store: BundleFileStore,
  fetchImpl: typeof fetch,
  filesUrl: (sha256: string) => string,
): Promise<DownloadOutcome> {
  const out = new Map<string, ArrayBuffer>()
  let fetchedBytes = 0
  for (const file of files) {
    const stored = await store.get(file.sha256, file.size)
    if (stored !== null) {
      out.set(file.name, await stored.arrayBuffer())
      continue
    }
    let res: Response
    try {
      res = await fetchImpl(filesUrl(file.sha256))
    } catch {
      return { kind: 'failed', reason: 'network' }
    }
    if (!res.ok) return { kind: 'failed', reason: `http_${res.status}` }
    let gz: ArrayBuffer
    try {
      gz = await res.arrayBuffer()
    } catch {
      return { kind: 'failed', reason: 'network' }
    }
    fetchedBytes += gz.byteLength
    if (gz.byteLength !== file.gzipSize || (await sha256Hex(gz)) !== file.gzipSha256) {
      return { kind: 'rejected', check: 'gzip_sha256' }
    }
    let bytes: ArrayBuffer
    try {
      bytes = await gunzip(gz)
    } catch {
      return { kind: 'rejected', check: 'sha256' }
    }
    if (bytes.byteLength !== file.size || (await sha256Hex(bytes)) !== file.sha256) {
      return { kind: 'rejected', check: 'sha256' }
    }
    try {
      await store.put(file.sha256, bytes)
    } catch {
      return { kind: 'failed', reason: 'storage' }
    }
    out.set(file.name, bytes)
  }
  return { kind: 'downloaded', files: out, fetchedBytes }
}
